import { useMemo, useState } from "react";
import { Avatar } from "@mui/material";
import { Link, useGetMany, useGetOne } from "react-admin";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { DateTime } from "luxon";
import { grants } from "../../backoffice.access_control";
import {
  type AutovioCalendarEvent,
  type TheoryLesson,
  type TheoryLessonStudentAttendance,
  canBeCanceled,
  canBeDeleted,
  isPendingInvitation,
  isTheoryLesson,
} from "../../model/autovioCalendarEvents";
import { formatDate, formatDateTime } from "../../utils/calendar";
import { autovioColors } from "../../misc/backofficeTheme";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { CancelAppointmentDialog } from "../../misc/calendar/CancelAppointmentDialog";
import { useDialog } from "../../hooks/useDialog";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { EditTheoryLessonForm } from "./EditTheoryLessonForm";
import { EventDialogProvider, useEventDialogContext } from "../../contexts/EventDialogContext";
import LoadingButton from "@mui/lab/LoadingButton";
import { DialogTopRightActions } from "../../misc/DialogTopRightActions";
import { useMenu } from "../../hooks/useMenu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HistoryIcon from "@mui/icons-material/History";
import FirestoreIcon from "@mui/icons-material/LocalFireDepartment";
import { firebaseApp } from "../../firebase";
import { CalendarEventStatusField } from "../../fields/CalendarEventStatusField";
import { CopyIdIntoClipboardMenuItem } from "../../menu-items/CopyIdIntoClipboardMenuItem";
import { CopyVogelIdIntoClipboardMenuItem } from "../../menu-items/CopyVogelIdIntoClipboardMenuItem";
import { DeleteCalendarEventDialog } from "../../misc/calendar/DeleteCalendarEventDialog";
import { EventChangeLog } from "./EventChangeLog";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { LessonProgressTimeline } from "./LessonProgressTimeline";
import { l10n } from "../../l10n";
import { renderLocation } from "../../fields/LocationField";
import { renderAddress } from "../../misc/AddressDisplay";
import { InstructorDisplayName } from "../../misc/InstructorDisplayName";
import { StudentLabels } from "../../misc/StudentLabels";
import { Student } from "../../providers/studentsProvider";
import { SetStudentAttendanceButtons } from "../../buttons/SetStudentAttendanceButtons";
import { AddStudentToTheoryLessonButton } from "../../buttons/AddStudentToTheoryLessonButton";
import type { AutovioCalendarEventHistory } from "../../providers/calendarEventHistoryProvider";
import { useRemoveStudentsFromTheoryLessonMutation } from "../../api/backoffice.api";

type DialogMode = "view" | "edit";

export function TheoryLessonDialog(props: {
  event: AutovioCalendarEvent;
  onClose: () => void;
  initialMode?: DialogMode;
}) {
  const { event, onClose, initialMode } = props;

  if (!isTheoryLesson(event)) {
    return null;
  }

  return (
    <EventDialogProvider event={event}>
      <_TheoryLessonDialog event={event} onClose={onClose} initialMode={initialMode} />
    </EventDialogProvider>
  );
}

const _TheoryLessonDialog = (props: { event: TheoryLesson; onClose: () => void; initialMode?: DialogMode }) => {
  const { event, onClose, initialMode } = props;
  const {
    isDialogOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog();
  const {
    isDialogOpen: isCancelDialogOpen,
    openDialog: openCancelDialog,
    closeDialog: closeCancelDialog,
  } = useDialog();
  const {
    isDialogOpen: isHistoryDialogOpen,
    openDialog: openHistoryDialog,
    closeDialog: closeHistoryDialog,
  } = useDialog();
  const canViewHistory = grants.has("viewCalendarEventEditHistory");
  const { data: history } = useGetOne<AutovioCalendarEventHistory>(
    "calendarEventHistory",
    { id: event.id, meta: { drivingSchoolId: event.drivingSchoolId } },
    { enabled: !!event.updatedAt && canViewHistory },
  );
  const changeCount = canViewHistory ? (history?.changes?.length ?? 0) : 0;

  const showDeleteButton = grants.has("calendarEventEdit") && canBeDeleted(event);
  const showCancelButton = grants.has("calendarEventEdit") && canBeCanceled(event);
  const showEditButton = (() => {
    if (event.deleted) {
      return false;
    }
    return grants.has("calendarEventEdit") || grants.has("TheoryLesson:edit");
  })();
  const [mode, setMode] = useState<DialogMode>(initialMode === "edit" && showEditButton ? "edit" : "view");

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" data-testid="EventDialog">
      <DialogTitle>
        <TheoryLessonDialogHeader event={event} mode={mode} />
      </DialogTitle>
      <TheoryLessonDialogTopRightActions
        event={event}
        showEditButton={mode === "view" && showEditButton}
        showEditForm={() => setMode("edit")}
        showHistoryButton={changeCount > 0}
        openHistoryDialog={openHistoryDialog}
      />
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        {mode === "view" && <LessonProgressTimeline event={event} />}
        <TheoryLessonDialogBody
          event={event}
          mode={mode}
          closeEditMode={() => setMode("view")}
          openCancelDialog={openCancelDialog}
          changeCount={changeCount}
          openHistoryDialog={openHistoryDialog}
        />
      </DialogContent>
      <TheoryLessonDialogActions
        event={event}
        mode={mode}
        showDeleteButton={showDeleteButton}
        openDeleteDialog={openDeleteDialog}
        showCancelButton={showCancelButton}
        openCancelDialog={openCancelDialog}
      />
      <DeleteCalendarEventDialog
        isOpen={isDeleteDialogOpen}
        calendarEvent={event}
        onClose={(calendarEventDeleted) => {
          closeDeleteDialog();
          if (calendarEventDeleted) {
            onClose();
          }
        }}
      />
      <CancelAppointmentDialog
        isOpen={isCancelDialogOpen}
        calendarEvent={event}
        onClose={(appointmentCancelled) => {
          closeCancelDialog();
          if (appointmentCancelled) {
            onClose();
          }
        }}
      />
      <TheoryLessonHistoryDialog
        event={event}
        history={history ?? null}
        isOpen={isHistoryDialogOpen}
        onClose={closeHistoryDialog}
      />
    </Dialog>
  );
};

const TheoryLessonDialogHeader = (props: { event: TheoryLesson; mode: DialogMode }) => {
  const { event, mode } = props;
  const title =
    mode === "edit"
      ? `${isPendingInvitation(event) ? "Einladung" : "Termin"} bearbeiten`
      : `Unterricht am ${formatDate(event.start)}`;

  const showStatus = mode === "view" && event.deleted;

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
        {showStatus ? (
          <div>
            <CalendarEventStatusField record={event} component="span" color="text.secondary" tooltip={false} />
            <br />
            <span>{title}</span>
          </div>
        ) : (
          <span>{title}</span>
        )}
      </Stack>
    </Stack>
  );
};

function TheoryLessonDialogTopRightActions(props: {
  event: TheoryLesson;
  showEditButton: boolean;
  showEditForm: () => void;
  showHistoryButton: boolean;
  openHistoryDialog: () => void;
}) {
  const { event, showEditButton, showEditForm, showHistoryButton, openHistoryDialog } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const showDeveloperOptions = grants.has("developer");
  const hasVisibleActions = showEditButton || showHistoryButton || showDeveloperOptions;

  if (!hasVisibleActions) {
    return null;
  }

  return (
    <DialogTopRightActions>
      <IconButton key="moreButton" aria-label="Weitere Aktionen" onClick={openMenu}>
        <MoreVertIcon sx={{ color: "text.secondary" }} />
      </IconButton>
      <Menu
        key="moreMenu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ "aria-labelledby": "dialog-actions" }}
      >
        {showEditButton && (
          <MenuItem
            onClick={() => {
              closeMenu();
              showEditForm();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{`${isPendingInvitation(event) ? "Einladung" : "Termin"} bearbeiten`}</ListItemText>
          </MenuItem>
        )}
        {showHistoryButton && (
          <MenuItem
            onClick={() => {
              closeMenu();
              openHistoryDialog();
            }}
          >
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Änderungshistorie</ListItemText>
          </MenuItem>
        )}
        {showDeveloperOptions && (
          <>
            <MenuItem
              onClick={() => {
                closeMenu();
                const { projectId } = firebaseApp.options;
                const path = `calendar_events/${event.id}`;
                const url = `https://console.firebase.google.com/project/${projectId}/firestore/data/${path.replaceAll("/", "~2F")}`;
                window.open(url, "_blank");
              }}
            >
              <ListItemIcon>
                <FirestoreIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Öffne in Firestore</ListItemText>
            </MenuItem>
            <CopyIdIntoClipboardMenuItem record={event} closeMenu={closeMenu} />
            <CopyVogelIdIntoClipboardMenuItem record={event} closeMenu={closeMenu} />
          </>
        )}
      </Menu>
    </DialogTopRightActions>
  );
}

function TheoryLessonHistoryDialog(props: {
  event: TheoryLesson;
  history: AutovioCalendarEventHistory | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { event, history, isOpen, onClose } = props;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md" data-testid="TheoryLessonHistoryDialog">
      <DialogTitle>Änderungshistorie</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>{isOpen && <EventChangeLog event={event} history={history} initiallyExpanded />}</DialogContent>
    </Dialog>
  );
}

const TheoryLessonDialogBody = (props: {
  event: TheoryLesson;
  mode: DialogMode;
  closeEditMode: () => void;
  openCancelDialog: () => void;
  changeCount: number;
  openHistoryDialog: () => void;
}) => {
  const { event, mode, closeEditMode, openCancelDialog, changeCount, openHistoryDialog } = props;

  const { registeredStudentIds, registeredAttendanceByStudentId } = useMemo(() => {
    const acceptedStudentEntries = Object.entries(event.students).filter(
      ([, attendance]) => attendance.rsvp === "accepted",
    );
    return {
      registeredStudentIds: acceptedStudentEntries.map(([studentId]) => studentId),
      registeredAttendanceByStudentId: Object.fromEntries(acceptedStudentEntries) as Record<
        string,
        TheoryLessonStudentAttendance
      >,
    };
  }, [event.students]);
  const { data: registeredStudents } = useGetMany<Student>("students", {
    ids: registeredStudentIds,
    meta: { drivingSchoolId: event.drivingSchoolId },
  });
  const [optimisticStudents, setOptimisticStudents] = useState<Array<Student>>([]);
  const [optimisticAttendanceByStudentId, setOptimisticAttendanceByStudentId] = useState<
    Record<string, TheoryLessonStudentAttendance>
  >({});
  const { data: createdBy } = useGetOne("users", { id: event.createdBy }, { enabled: !!event.createdBy });
  const sortedRegisteredStudents = useMemo(() => {
    if ((registeredStudents || []).length === 0 && optimisticStudents.length === 0) {
      return [];
    }
    const displayedAttendanceByStudentId = { ...optimisticAttendanceByStudentId, ...registeredAttendanceByStudentId };
    const displayedRegisteredStudents = [
      ...(registeredStudents ?? []),
      ...optimisticStudents.filter((student) => !(registeredStudents ?? []).some((it) => it.id === student.id)),
    ];
    return displayedRegisteredStudents
      .map((student) => ({ student, attendance: displayedAttendanceByStudentId[student.id] }))
      .filter((it) => !!it.attendance)
      .sort((a, b) => a.student.name.localeCompare(b.student.name));
  }, [optimisticStudents, optimisticAttendanceByStudentId, registeredStudents, registeredAttendanceByStudentId]);
  const { studentsTempAttendance } = useEventDialogContext();

  if (mode === "edit") {
    return <EditTheoryLessonForm event={event} closeEditMode={closeEditMode} openCancelDialog={openCancelDialog} />;
  }

  const formattedTimeRange = `${event.start.setZone("Europe/Berlin").toFormat("H:mm")} - ${event.end
    .setZone("Europe/Berlin")
    .toFormat("H:mm")} Uhr`;
  const formattedDate = event.start.setZone("Europe/Berlin").setLocale("de-DE").toFormat("ccc, dd.MM.yyyy");
  const formattedLocation = renderLocation(event);
  const formattedLocationAddress =
    event.location.type === "OnlineMeeting" ? event.location.url : renderAddress(event.location, { oneLine: true });
  const canAddStudents = grants.has("addStudentToTheoryLesson");
  const canRemoveStudents = grants.has("removeStudentFromTheoryLesson") && DateTime.now() < event.start;
  const changeCountText = changeCount === 1 ? "1 Änderung" : `${changeCount} Änderungen`;
  const getAttendanceIndicatorColor = (studentId: string, attendance: TheoryLessonStudentAttendance) => {
    const temporaryAttendance = studentsTempAttendance[studentId];
    const attended = temporaryAttendance === true || (temporaryAttendance === undefined && attendance.attended);
    const noShow = temporaryAttendance === false || (temporaryAttendance === undefined && attendance.noShow);

    if (attended) {
      return autovioColors.green;
    }
    if (noShow) {
      return autovioColors.red;
    }
    return "transparent";
  };

  return (
    <Stack direction="column" spacing="1em" sx={{ mt: "20px" }}>
      <Stack spacing="20px">
        <Row
          sx={{
            alignItems: "flex-start",
            justifyContent: "flex-start",
            flexWrap: "wrap",
            borderRadius: "16px",
            backgroundColor: "action.hover",
            px: "24px",
            pt: "16px",
            pb: "12px",
            columnGap: "40px",
            rowGap: "16px",
            color: "text.secondary",
            "& a": { color: "inherit" },
          }}
        >
          <Row sx={{ alignItems: "flex-start", flex: "1 1 240px", minWidth: 0, gap: "12px" }}>
            <Box
              sx={{
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                backgroundColor: "#d16fad",
                mt: "6px",
                flexShrink: 0,
              }}
            />
            <Column sx={{ alignItems: "flex-start", minWidth: 0, gap: "2px" }}>
              <Typography variant="h6" fontWeight={700} fontSize="14px">
                {l10n.lectureXxx(event)}
              </Typography>
              <Typography variant="h6" fontWeight={400} fontSize="14px" data-testid="theory-lesson-date-time-range">
                {formattedTimeRange}
              </Typography>
            </Column>
          </Row>

          <Row sx={{ alignItems: "flex-start", flex: "1 1 240px", minWidth: 0, gap: "12px" }}>
            <CalendarTodayOutlinedIcon sx={{ mt: "4px", flexShrink: 0, fontSize: "18px" }} />
            <Column sx={{ alignItems: "flex-start", minWidth: 0, gap: "2px" }}>
              <Typography variant="h6" fontWeight={700} fontSize="14px">
                {formattedDate}
              </Typography>
              <Typography variant="h6" fontWeight={400} fontSize="14px">
                {formattedTimeRange}
              </Typography>
            </Column>
          </Row>

          <Row sx={{ alignItems: "flex-start", flex: "1 1 240px", minWidth: 0, gap: "12px" }}>
            <PlaceOutlinedIcon sx={{ mt: "4px", flexShrink: 0, fontSize: "18px" }} />
            <Column sx={{ alignItems: "flex-start", minWidth: 0, gap: "2px" }}>
              <Typography variant="h6" fontWeight={700} fontSize="14px" sx={{ overflowWrap: "anywhere" }}>
                {formattedLocation || "-"}
              </Typography>
              <Typography variant="h6" fontWeight={400} fontSize="14px" sx={{ overflowWrap: "anywhere" }}>
                {formattedLocationAddress || "-"}
              </Typography>
            </Column>
          </Row>

          <Row sx={{ alignItems: "flex-start", flex: "1 1 240px", minWidth: 0, gap: "12px" }}>
            <PersonOutlineOutlinedIcon sx={{ mt: "4px", flexShrink: 0, fontSize: "18px" }} />
            <Column sx={{ alignItems: "flex-start", minWidth: 0, gap: "2px" }}>
              <InstructorDisplayName
                instructorId={event.instructorId}
                sx={{ fontWeight: 700, fontSize: "14px", overflowWrap: "anywhere" }}
              />
              <Typography variant="h6" fontWeight={400} fontSize="14px">
                Dozent/Fahrlehrer
              </Typography>
            </Column>
          </Row>
        </Row>

        <Column sx={{ border: 1, borderColor: "divider", borderRadius: "12px", overflow: "hidden" }}>
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    backgroundColor: "action.hover",
                    color: "text.secondary",
                    fontWeight: 700,
                    py: "10px",
                    px: "16px",
                  }}
                >
                  Teilnehmer
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    backgroundColor: "action.hover",
                    color: "text.secondary",
                    fontWeight: 700,
                    py: "10px",
                    px: "8px",
                    width: "170px",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  Anwesenheit bestätigt
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    backgroundColor: "action.hover",
                    p: 0,
                    width: "36px",
                  }}
                />
                <TableCell
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    backgroundColor: "action.hover",
                    p: 0,
                    width: "16px",
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRegisteredStudents.length === 0 && (
                <TableRow>
                  <TableCell sx={{ px: "16px", py: "12px", borderBottom: 0 }}>
                    <Typography variant="body2" sx={{ color: "text.disabled" }}>
                      Keine angemeldeten Fahrschüler.
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 0, width: "170px" }} />
                  <TableCell sx={{ borderBottom: 0, p: 0, width: "36px" }} />
                  <TableCell sx={{ borderBottom: 0, p: 0, width: "16px" }} />
                </TableRow>
              )}

              {sortedRegisteredStudents.map(({ student, attendance }, index) => (
                <TableRow key={student.id}>
                  <TableCell
                    sx={{
                      px: "16px",
                      py: "8px",
                      borderBottom: index === sortedRegisteredStudents.length - 1 ? 0 : 1,
                      borderColor: "divider",
                    }}
                  >
                    <Link
                      to={`/students/${student.id}`}
                      sx={{ display: "flex", alignItems: "center", color: "inherit" }}
                    >
                      <Avatar src={student.avatarUrl} sx={{ width: 32, height: 32 }} />
                      <Column sx={{ ml: "12px" }}>
                        <StudentLabels student={student} size="small" />
                        <Typography variant="body2">{student.name}</Typography>
                      </Column>
                    </Link>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "170px",
                      px: "8px",
                      py: "0",
                      textAlign: "right",
                      borderBottom: index === sortedRegisteredStudents.length - 1 ? 0 : 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <SetStudentAttendanceButtons studentId={student.id} studentAttendance={attendance} />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "36px",
                      p: 0,
                      textAlign: "center",
                      borderBottom: index === sortedRegisteredStudents.length - 1 ? 0 : 1,
                      borderColor: "divider",
                    }}
                  >
                    {canRemoveStudents && <TheoryLessonStudentActionsMenu event={event} studentId={student.id} />}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "16px",
                      p: 0,
                      background: `linear-gradient(to right, transparent 0, transparent 8px, ${getAttendanceIndicatorColor(
                        student.id,
                        attendance,
                      )} 8px, ${getAttendanceIndicatorColor(student.id, attendance)} 16px)`,
                      borderBottom: index === sortedRegisteredStudents.length - 1 ? 0 : 1,
                      borderColor: "divider",
                    }}
                  />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Column>
      </Stack>

      <Row sx={{ justifyContent: "space-between", alignItems: "center", mt: "10px" }}>
        <Typography variant="body2" sx={{ color: "text.disabled" }}>
          {`${sortedRegisteredStudents.length}/${event.maxStudents} Teilnehmer • Erstellt am ${formatDateTime(
            event.createdAt,
          )} von ${createdBy?.name ?? "..."}`}
          {changeCount > 0 && (
            <>
              {" • "}
              <Box
                component="button"
                type="button"
                onClick={openHistoryDialog}
                sx={{
                  p: 0,
                  border: 0,
                  background: "transparent",
                  color: "inherit",
                  cursor: "pointer",
                  font: "inherit",
                  textDecoration: "underline",
                }}
              >
                {changeCountText}
              </Box>
            </>
          )}
        </Typography>
        {canAddStudents && (
          <AddStudentToTheoryLessonButton
            theoryLesson={event}
            buttonLabel="Teilnehmer"
            onAddStudent={(student) => {
              setOptimisticStudents((students) => [...students.filter((it) => it.id !== student.id), student]);
              setOptimisticAttendanceByStudentId((attendanceByStudentId) => ({
                ...attendanceByStudentId,
                [student.id]: { agreedCancellationPolicy: null, rsvp: "accepted" },
              }));
            }}
            onAddStudentError={(student) => {
              setOptimisticStudents((students) => students.filter((it) => it.id !== student.id));
              setOptimisticAttendanceByStudentId((attendanceByStudentId) => {
                const { [student.id]: _student, ...remainingAttendanceByStudentId } = attendanceByStudentId;
                return remainingAttendanceByStudentId;
              });
            }}
          />
        )}
      </Row>
    </Stack>
  );
};

function TheoryLessonStudentActionsMenu(props: { event: TheoryLesson; studentId: string }) {
  const { event, studentId } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const [isRemovingStudent, setIsRemovingStudent] = useState(false);
  const removeStudentsFromTheoryLessonMutation = useRemoveStudentsFromTheoryLessonMutation();

  const removeStudentFromTheoryLesson = async () => {
    setIsRemovingStudent(true);
    await runSafely(
      () =>
        removeStudentsFromTheoryLessonMutation.mutateAsync({
          calendarEventUid: event.id,
          studentUids: [studentId],
        }),
      {
        successMessage: "Teilnehmer erfolgreich kostenfrei entfernt.",
        errorMessage: "Fehler beim kostenfreien Entfernen des Teilnehmers.",
      },
    );
    setIsRemovingStudent(false);
    closeMenu();
  };

  return (
    <>
      <IconButton
        aria-label="Teilnehmeraktionen"
        disabled={isRemovingStudent}
        onClick={openMenu}
        size="small"
        sx={{ transform: "translateX(-4px)" }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem disabled={isRemovingStudent} onClick={() => void removeStudentFromTheoryLesson()}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {isRemovingStudent ? "Teilnehmer wird entfernt ..." : "Teilnehmer kostenfrei entfernen"}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

function TheoryLessonDialogActions(props: {
  event: TheoryLesson;
  mode: DialogMode;
  showDeleteButton: boolean;
  openDeleteDialog: () => void;
  showCancelButton: boolean;
  openCancelDialog: () => void;
}) {
  const { event, mode, showDeleteButton, openDeleteDialog, showCancelButton, openCancelDialog } = props;
  const { isSaving, studentsTempAttendance, handleSaveStudentsAttendance } = useEventDialogContext();

  if (mode === "edit") return null;

  const showSaveAttendanceButton = Object.keys(studentsTempAttendance).length > 0;

  if (!(showSaveAttendanceButton || showDeleteButton || showCancelButton)) {
    return null;
  }

  return (
    <DialogActions sx={{ mt: "20px" }}>
      {showSaveAttendanceButton && (
        <LoadingButton loading={isSaving} variant="contained" onClick={handleSaveStudentsAttendance}>
          Anwesenheit speichern
        </LoadingButton>
      )}
      {showDeleteButton && (
        <Button
          variant="outlined"
          sx={{ height: "40px" /* ... same height as other buttons in dialog. */ }}
          startIcon={<DeleteIcon />}
          onClick={openDeleteDialog}
        >
          {`${isPendingInvitation(event) ? "Einladung" : "Termin"} löschen`}
        </Button>
      )}
      {showCancelButton && (
        <Button
          variant="outlined"
          sx={{ height: "40px" /* ... same height as other buttons in dialog. */ }}
          startIcon={<CancelIcon />}
          onClick={openCancelDialog}
        >
          Termin absagen
        </Button>
      )}
    </DialogActions>
  );
}
